import { ReactNode } from "react";

import { renderHighlightedPreview } from "@/utils/renderHighlightedPreview";
import { truncate } from "@/utils/strings";
import type { IntentHighlightAnnotation } from "@/types/requests";

const PREVIEW_UNAVAILABLE = "<preview temporarily unavailable>";

export const REQUEST_LOG_INPUT_PREVIEW_LABEL = "Prompt: ";

export const stripHighlightMarkup = (text: string): string => text.replace(/<\/?em>/gi, "");

/** Remove OpenSearch transcript role markers like `[system]:` / `[user]:` from previews. */
export const stripInputTextRolePrefixes = (text: string): string =>
  text.replace(/\[([a-z_]+)\]:\s*/gi, "").trim();

/** Always prefer OpenSearch input_text (full transcript); fall back to DB prompt_preview. */
export const getRequestLogInputPreviewText = (item: {
  input_text?: string | null;
  prompt_preview?: string | null;
}): string => {
  const raw = item.input_text?.trim() || item.prompt_preview?.trim() || "";
  return raw ? stripInputTextRolePrefixes(raw) : "";
};

export const formatRequestLogPreviewDisplay = (
  text: string | undefined | null,
  {
    fallback = PREVIEW_UNAVAILABLE,
    maxPlainTextLength = 200,
    highlightAnnotations = null,
  }: {
    fallback?: string;
    maxPlainTextLength?: number;
    highlightAnnotations?: IntentHighlightAnnotation[] | null;
  } = {},
): { display: ReactNode; title: string } => {
  const value = text?.trim();
  if (!value) {
    return { display: fallback, title: fallback };
  }

  const title = stripHighlightMarkup(value);
  if (/<em>/i.test(value)) {
    return { display: renderHighlightedPreview(value, highlightAnnotations), title };
  }

  const truncated = truncate(value, maxPlainTextLength);
  return { display: truncated, title: truncated };
};
